import { Image, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Blog.module.scss';

import ShortDescriptionFieldWrapper from 'components/helpers/ShortDescriptionFieldWrapper';
import { BlogConstants, BlogPostData, BlogPostsGridProps } from 'models/blog-types';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import BlogTopicsList from './BlogTopicsList';
import Icon from 'components/helpers/Icon';
import { chunkArray, getLanguageBasedUrl } from 'lib/utils';
import { useRouter } from 'next/router';
import { getBlogPosts } from './API';
import { getDatabaseName } from './Functions';

const BlogPostsGrid: React.FC<BlogPostsGridProps> = (props) => {
  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();
  const { language, itemId, pageState } = sitecoreContext;
  const cx = classNames.bind(styles);

  const databaseName = getDatabaseName(sitecoreContext);
  const [postsRows, setPostsRows] = useState<BlogPostData[][]>();
  const [blogPostsDataLoaded, setBlogPostsDataLoaded] = useState<boolean>(false);
  const isNormalPageMode = pageState === 'normal';

  const {
    cardColumns,
    blogPostsRequest,
    className,
    titleHeadingLevelValue,
    title,
    description,
    titleTextStyleValue,
    excludeContextItem,
  } = props;

  useEffect(() => {
    const loadBlogPostsFlag =
      (!isNormalPageMode || router.isReady) && blogPostsRequest.postsToShow && !blogPostsDataLoaded;

    if (!isNormalPageMode) {
      console.log(
        `BlogPostsGrid - useEffect - isNormalPageMode: ${isNormalPageMode} isReady: ${router.isReady} postsToShow: ${blogPostsRequest.postsToShow} blogPostsDataLoaded: ${blogPostsDataLoaded}`
      );
      console.log(`BlogPostsGrid - loadBlogPostsFlag: ${loadBlogPostsFlag}`);
    }

    if (loadBlogPostsFlag) {
      blogPostsRequest.rootItemId = BlogConstants.BlogPostsRoot;
      blogPostsRequest.language = language;
      blogPostsRequest.database = databaseName;

      if (excludeContextItem) {
        blogPostsRequest.excludeItemId = itemId;
      }

      getBlogPosts(blogPostsRequest).then((data) => {
        const nonNullPosts = data?.posts?.filter((x) => x !== null) || [];
        const chunks = chunkArray<BlogPostData>(nonNullPosts, cardColumns);

        setPostsRows(chunks);
        setBlogPostsDataLoaded(true);
      });
    }
  });

  // Setting is configured to not show any posts - do not render anything
  if (!blogPostsRequest.postsToShow) {
    return <></>;
  }

  // API is called but no data is returned, hide everything
  if (blogPostsDataLoaded && postsRows?.length === 0) {
    return <></>;
  }

  return (
    <section className={className}>
      {!blogPostsDataLoaded && <Loader variant={LoaderVariant.Spinner} className={cx('loader')} />}

      {blogPostsDataLoaded && title && (
        <Text
          tag={titleHeadingLevelValue}
          field={title}
          encode={sitecoreContext.pageEditing}
          className={cx({ [titleTextStyleValue || '']: true }, 'heading')}
        />
      )}

      {blogPostsDataLoaded && description && <RichTextWrapper field={description} />}

      {blogPostsDataLoaded &&
        postsRows?.map((row, rowIndex) => (
          <div key={`blog-post-${rowIndex}`} className={cx('blog-post-grid', `blog-post-grid__grid${cardColumns}`)}>
            {row.map((post: BlogPostData, columnIndex) => (
              <React.Fragment key={`${rowIndex}-${columnIndex}`}>
                <div className={cx('blog-post-card__photo')}>
                  {post.blogPhoto && <Image field={{ src: post.blogPhoto }} />}
                </div>

                <div className={cx('blog-post-card__topics')}>
                  <BlogTopicsList blogTopicsData={post.blogTopics} language={language} />
                </div>

                <div className={cx('blog-post-card__heading')}>
                  <div>
                    <a href={getLanguageBasedUrl(post.url, language)}> {post.blogHeading} </a>
                    <Icon variant="carat" />
                  </div>
                </div>

                <div className={cx('blog-post-card__summary')}>
                  <ShortDescriptionFieldWrapper
                    textField={{ value: post.blogShortDescription }}
                    stringLength={+BlogConstants.BlogSummaryChars}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        ))}
    </section>
  );
};

export default BlogPostsGrid;
